import React from 'react';
import {
  Browser,
  ChatsTeardrop,
  CircleNotch,
  File,
  FileDoc,
  FilePdf,
  FileText,
  Folder,
  Image,
  Video,
  X,
} from '@phosphor-icons/react';

import type { ContextPill, ContextPillIconType } from './types';

interface ContextPillBadgeProps {
  pill: ContextPill;
  onRemove?: (pill: ContextPill) => void;
}

const ICON_MAP: Record<ContextPillIconType, React.ElementType> = {
  image: Image,
  video: Video,
  pdf: FilePdf,
  doc: FileDoc,
  file: File,
  channel: ChatsTeardrop,
  collection: Folder,
  article: FileText,
  page: Browser,
};

export const ContextPillBadge = ({ pill, onRemove }: ContextPillBadgeProps) => {
  const Icon = pill.iconType ? ICON_MAP[pill.iconType] : null;
  const fillPercent = pill.loading ? (pill.progress ?? 0) : 100;

  return (
    <span className="relative inline-flex items-center gap-0.5 overflow-hidden rounded-full border border-muted px-2 py-1 text-xs text-muted-foreground">
      {/* Progress fill — same colour as the resting bg so it disappears when complete */}
      <span
        className="absolute inset-0 bg-muted transition-[width] duration-300 ease-out"
        style={{ width: `${fillPercent}%` }}
      />
      {/* Content */}
      <span className="relative flex items-center gap-0.5">
        {pill.loading ? (
          <CircleNotch className="h-3 w-3 shrink-0 mr-0.5 animate-spin" />
        ) : (
          Icon && <Icon className="h-3 w-3 shrink-0 mr-0.5" />
        )}
        {pill.label}
        {onRemove && (
          <button
            type="button"
            className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-muted-foreground/20"
            onClick={() => onRemove(pill)}
          >
            <X className="h-2.5 w-2.5" />
          </button>
        )}
      </span>
    </span>
  );
};
